import { ITranslations } from '@model';
import { ELogLevel, LoggerType } from '@services/errorService';
import { EPage } from '@types';
import { isObjectEmpty } from '@utils/general';

import { ETranslationKeys, LOCALIZATION_CONSTANTS } from './model';

export const getPageLocalizationKeys = (page: EPage): ETranslationKeys[] => LOCALIZATION_CONSTANTS[page];

export const getMissingKeys = (page: EPage, callLogger: LoggerType): ETranslationKeys[] => {
  const keys = getPageLocalizationKeys(page);

  return keys.filter((key) => !getTranslationsFromSessionStorage(key, callLogger));
};

export const getPageTranslationsFromSessionStorage = (page: EPage, callLogger: LoggerType): ITranslations => {
  const keys = getPageLocalizationKeys(page);
  const translationsByKey = keys.map((key) => getTranslationsFromSessionStorage(key, callLogger));

  return translationsByKey.reduce(
    (aggregator, translations) => ({ ...translations, ...aggregator }),
    {},
  ) as ITranslations;
};

export const setPageTranslationsToSessionStorage = (
  translations: ITranslations,
  callLogger: LoggerType,
  page: EPage,
) => {
  const keys = getPageLocalizationKeys(page);

  keys.forEach((key) => {
    const filteredTranslations = Object.entries(translations)
      .filter(([translationKey]) => translationKey.startsWith(key))
      .reduce(
        (aggregator: ITranslations, [translationKey, value]): ITranslations => ({
          ...aggregator,
          [translationKey]: value,
        }),
        {},
      );

    if (isObjectEmpty(filteredTranslations)) {
      return;
    }

    setTranslationsToSessionStorage(filteredTranslations, callLogger, key);
  });
};

export const getTranslationsFromSessionStorage = (
  key: ETranslationKeys,
  callLogger: LoggerType,
): ITranslations | null => {
  if (!key || !isSessionStorageAvailable()) {
    return null;
  }

  try {
    const translation = sessionStorage.getItem(key);
    return translation ? (JSON.parse(translation) as ITranslations) : null;
  } catch (error) {
    callLogger({ logLevel: ELogLevel.ERROR, logError: error as Error });
    return null;
  }
};

export const setTranslationsToSessionStorage = (
  translations: ITranslations,
  callLogger: LoggerType,
  key: ETranslationKeys,
) => {
  if (key && isSessionStorageAvailable()) {
    try {
      sessionStorage.setItem(key, JSON.stringify(translations));
    } catch (error) {
      callLogger({ logLevel: ELogLevel.ERROR, logError: error as Error });
    }
  }
};

const isSessionStorageAvailable = () => {
  try {
    const testKey = '__test__';
    sessionStorage.setItem(testKey, '1');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};
